import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "A balance sheet is a snapshot of what a company owns, owes, and what's left for shareholders at a single point in time. It has three sections: assets, liabilities, and equity. The fundamental rule is Assets = Liabilities + Equity. If you can read those three numbers, you can spot most of the financial-health red flags before they hit the news.";

const FAQS = [
  { q: 'How is a balance sheet different from an income statement?', a: 'The income statement covers a period (a quarter, a year). The balance sheet is a snapshot at a single moment, usually the last day of the period. One shows flow; the other shows position.' },
  { q: 'What does "current" mean on a balance sheet?', a: 'Current assets convert to cash within 12 months (cash, receivables, inventory). Current liabilities are due within 12 months. The ratio between them — the current ratio — is a quick liquidity check.' },
  { q: 'What is goodwill?', a: 'Goodwill is the premium a company paid above the fair value of an acquired business\'s assets. Large goodwill balances can be written down, which hurts reported earnings even if no cash leaves the company.' },
  { q: 'What\'s a healthy current ratio?', a: 'Above 1.0 means the company can cover short-term liabilities with short-term assets. Above 2.0 is conservative. Below 1.0 is a warning unless the company has reliable cash inflows (like a subscription business).' },
  { q: 'Why does shareholder equity sometimes go negative?', a: 'When accumulated losses or large buybacks exceed total contributed capital, equity can turn negative. McDonald\'s and Boeing have both shown negative equity at times. Analyse why before assuming distress.' },
];

export default function HowToReadABalanceSheetPage() {
  return (
    <BlogArticleLayout
      slug="how-to-read-a-balance-sheet"
      title="How to Read a Balance Sheet (Beginner's Guide)"
      description="A balance sheet is just three sections: assets, liabilities, and equity. Here's how to read one in five minutes without any accounting background."
      keywords="how to read a balance sheet, balance sheet basics, balance sheet for beginners, assets liabilities equity, current ratio, balance sheet explained"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['debt-to-equity-ratio', 'what-is-roe', 'stock-picking']}
    >
      <section>
        <h2>What is a balance sheet?</h2>
        <p>
          A balance sheet is a financial statement that lists everything a company owns (assets), everything it owes (liabilities), and what's left for shareholders (equity).
          It always obeys the equation: <strong>Assets = Liabilities + Equity</strong>. That balance has to be exact — hence the name.
        </p>
        <p>
          Public companies publish a balance sheet every quarter as part of their 10-Q or 10-K filings.
        </p>
      </section>

      <section>
        <h2>What goes in the assets section?</h2>
        <p>
          Assets are split into "current" (convertible to cash within a year) and "non-current" (longer term). Common line items:
        </p>
        <ul>
          <li><strong>Cash and equivalents</strong> — bank balances and short-term investments.</li>
          <li><strong>Accounts receivable</strong> — money customers owe but haven't paid yet.</li>
          <li><strong>Inventory</strong> — products sitting on shelves or in warehouses.</li>
          <li><strong>Property, plant & equipment (PP&E)</strong> — buildings, machinery, vehicles.</li>
          <li><strong>Goodwill and intangibles</strong> — premiums paid in past acquisitions, plus brands and patents.</li>
        </ul>
      </section>

      <section>
        <h2>What goes in the liabilities section?</h2>
        <p>
          Same split — current and non-current. Common items:
        </p>
        <ul>
          <li><strong>Accounts payable</strong> — invoices the company owes suppliers.</li>
          <li><strong>Short-term debt</strong> — borrowings due within 12 months.</li>
          <li><strong>Long-term debt</strong> — bonds and loans due beyond 12 months.</li>
          <li><strong>Deferred revenue</strong> — cash collected for services not yet delivered (huge for SaaS).</li>
          <li><strong>Lease obligations</strong> — future rent commitments now required to be on the balance sheet.</li>
        </ul>
      </section>

      <section>
        <h2>What does shareholder equity represent?</h2>
        <p>
          Equity is the residual: assets minus liabilities. It's the accounting value of what would be left for shareholders if the company sold everything and paid off every debt.
          Key components:
        </p>
        <ul>
          <li><strong>Common stock + paid-in capital</strong> — money raised from issuing shares.</li>
          <li><strong>Retained earnings</strong> — cumulative profits the company has kept rather than paid out.</li>
          <li><strong>Treasury stock</strong> — shares the company has bought back.</li>
        </ul>
        <p>
          Equity is the denominator in <Link to="/blog/what-is-roe">return on equity</Link> and <Link to="/blog/debt-to-equity-ratio">debt-to-equity</Link>.
        </p>
      </section>

      <section>
        <h2>What should you check first when reading a balance sheet?</h2>
        <p>
          Five-minute checklist:
        </p>
        <ul>
          <li><strong>Cash position.</strong> How much cash is on hand vs short-term debt? More cash than short-term debt = comfortable.</li>
          <li><strong>Current ratio.</strong> Current assets ÷ current liabilities. Above 1.0 is the minimum.</li>
          <li><strong>Total debt.</strong> Compare to equity (D/E ratio) and to operating income.</li>
          <li><strong>Goodwill.</strong> If goodwill is more than 50% of total assets, the business is built on past acquisitions and may face write-downs.</li>
          <li><strong>Equity trend.</strong> Is equity growing year over year? Compounding equity is one of the strongest signs of a healthy business.</li>
        </ul>
        <p>
          Pair this with the income statement (see <Link to="/blog/revenue-vs-earnings">revenue vs earnings</Link>) and the cash flow statement to get the full picture.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
